import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Text,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import { User, Rock, Issue, Metric, ActionItem } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatDateForInput } from '../../../../utils/date.utils';

interface AddActionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: any) => Promise<void>;
  currentUserId?: string;
  users?: User[]; // Optional - component now uses eosUsers from AuthContext
  rocks: Rock[];
  issues: Issue[];
  metrics: Metric[];
  editingActionItem?: ActionItem | null;
}

/**
 * Add Action Item Modal Component
 * Uses Formik for form state and Yup for validation
 * Migrated from Tailwind to Chakra UI
 */
export function AddActionItemModal({
  isOpen,
  onClose,
  onSave,
  currentUserId,
  users,
  rocks,
  issues,
  metrics,
  editingActionItem,
}: AddActionItemModalProps) {
  const { eosUsers = [] } = useAuth();
  // Validation schema using Yup
  const ActionItemSchema = Yup.object().shape({
    description: Yup.string()
      .min(10, 'Description must be at least 10 characters')
      .max(500, 'Description must be 500 characters or less')
      .required('Description is required'),
    assignedToUserId: Yup.string().required('Assigned To is required'), // Renamed from Owner
    ownerId: Yup.string().nullable(), // This was "Assign To (Optional)"
    dueDate: Yup.date().required('Due date is required'),
    linkType: Yup.string(),
    linkedRockId: Yup.string().when('linkType', {
      is: 'rock',
      then: (schema) => schema.required('Please select a Rock'),
    }),
    linkedIssueId: Yup.string().when('linkType', {
      is: 'issue',
      then: (schema) => schema.required('Please select an Issue'),
    }),
    linkedMetricId: Yup.string().when('linkType', {
      is: 'metric',
      then: (schema) => schema.required('Please select a Metric'),
    }),
  });

  const initialValues = {
    description: editingActionItem?.description || '',
    assignedToUserId: editingActionItem?.assignedToUserId || '', // Renamed
    ownerId: editingActionItem?.ownerId || '', // Renamed
    dueDate: editingActionItem?.dueDate
      ? formatDateForInput(editingActionItem.dueDate)
      : '',
    linkType: editingActionItem?.linkedRockId
      ? 'rock'
      : editingActionItem?.linkedIssueId
      ? 'issue'
      : editingActionItem?.linkedMetricId
      ? 'metric'
      : 'none',
    linkedRockId: editingActionItem?.linkedRockId || '',
    linkedIssueId: editingActionItem?.linkedIssueId || '',
    linkedMetricId: editingActionItem?.linkedMetricId || '',
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const formData = {
        description: values.description,
        assignedToUserId: values.assignedToUserId, // Renamed
        ownerId: values.ownerId || null, // Renamed
        dueDate: values.dueDate,
        linkedRockId: values.linkType === 'rock' ? values.linkedRockId : null,
        linkedIssueId: values.linkType === 'issue' ? values.linkedIssueId : null,
        linkedMetricId: values.linkType === 'metric' ? values.linkedMetricId : null,
      };
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving action item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{editingActionItem ? 'Edit Action Item' : 'Add New Action Item'}</ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={initialValues}
          validationSchema={ActionItemSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, errors, touched, setFieldValue }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  {/* 1. Description */}
                  <Field name="description">
                    {({ field }: any) => (
                      <FormControl isInvalid={!!(errors.description && touched.description)}>
                        <FormLabel>Description *</FormLabel>
                        <Textarea
                          {...field}
                          placeholder="Enter a clear, actionable description (min 10 characters)"
                          rows={3}
                        />
                        <HStack justify="space-between">
                          <FormErrorMessage>{errors.description}</FormErrorMessage>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {values.description.length}/500
                          </Text>
                        </HStack>
                      </FormControl>
                    )}
                  </Field>

                  {/* 2. Assigned To (Was Owner) */}
                  <Field name="assignedToUserId">
                    {({ field }: any) => (
                      <FormControl isInvalid={!!(errors.assignedToUserId && touched.assignedToUserId)}>
                        <FormLabel>Assigned To *</FormLabel>
                        <Select {...field} placeholder="Select responsible person">
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.assignedToUserId}</FormErrorMessage>
                        <Text fontSize="xs" color="gray.500" mt={1}>Select the person responsible for this action item.</Text>
                      </FormControl>
                    )}
                  </Field>

                  {/* 3. Owner (Was Assign To (Optional)) */}
                  <Field name="ownerId">
                    {({ field }: any) => (
                      <FormControl isInvalid={!!(errors.ownerId && touched.ownerId)}>
                        <FormLabel>Owner (Optional)</FormLabel>
                        <Select {...field} placeholder="Select owner">
                          <option value="">None</option>
                          {eosUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.ownerId}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* 4. Due Date */}
                  <Field name="dueDate">
                    {({ field }: any) => (
                      <FormControl isInvalid={!!(errors.dueDate && touched.dueDate)}>
                        <FormLabel>Due Date *</FormLabel>
                        <Input {...field} type="date" />
                        <FormErrorMessage>{errors.dueDate}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* 5. Link To (Optional) --- NEW --- */}
                  <FormControl>
                    <FormLabel>Link To (Optional)</FormLabel>
                    <RadioGroup
                      value={values.linkType}
                      onChange={(val) => setFieldValue('linkType', val)}
                    >
                      <Stack direction="row" spacing={4}>
                        <Radio value="none">None</Radio>
                        <Radio value="rock">Rock</Radio>
                        <Radio value="issue">Issue</Radio>
                        <Radio value="metric">Metric</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {/* 6. Conditional Linked Selects --- NEW --- */}
                  {values.linkType === 'rock' && (
                    <Field name="linkedRockId">
                      {({ field }: any) => (
                        <FormControl isInvalid={!!(errors.linkedRockId && touched.linkedRockId)}>
                          <Select {...field} placeholder="-- Select Rock --">
                            {rocks.map((rock) => (
                              <option key={rock.id} value={rock.id}>{rock.rock}</option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.linkedRockId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {values.linkType === 'issue' && (
                    <Field name="linkedIssueId">
                      {({ field }: any) => (
                        <FormControl isInvalid={!!(errors.linkedIssueId && touched.linkedIssueId)}>
                          <Select {...field} placeholder="-- Select Issue --">
                            {issues.map((issue) => (
                              <option key={issue.id} value={issue.id}>{issue.name}</option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.linkedIssueId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}

                  {values.linkType === 'metric' && (
                    <Field name="linkedMetricId">
                      {({ field }: any) => (
                        <FormControl isInvalid={!!(errors.linkedMetricId && touched.linkedMetricId)}>
                          <Select {...field} placeholder="-- Select Metric --">
                            {metrics.map((metric) => (
                              <option key={metric.id} value={metric.id}>{metric.metricName || 'Unknown Metric'}</option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.linkedMetricId}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  {editingActionItem ? 'Save Changes' : 'Save Action Item'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}
